<template>

    <div class="pa-0 fill-height">

        <v-card class="pt-1" flat>

            <material-header
                :loading="loading || saving"
                title="Email template editor"
                info="Customize emails to match your brand"
                help="subpanel-email-template"
                icon="mdi-card-text-outline">

                <template slot="buttons">

                    <!-- SAVE TRANSLATIONS BUTTON -->
                    <v-scale-transition>
                        <v-btn v-show="unsavedTranslations || unsavedSettings"
                               color="primary"
                               :loading="saving"
                               @click="saveChanges"
                               class="mr-2">
                            Save changes?
                        </v-btn>
                    </v-scale-transition>

                    <v-tooltip bottom color="primary" :open-delay="1000">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" fab small class="ml-2 elevation-2" color="red" dark @click="exitClicked">
                                <v-icon> mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Exit editor</span>
                    </v-tooltip>

                </template>

            </material-header>

            <v-container fluid v-if="sessionActive">

                <v-row v-if="!hasPermission(minPermission)">
                    <v-col cols="12">
                        <v-alert type="error" class="body-2">
                            Your user account doesn't have sufficient permissions for this feature
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row v-if="hasPermission(minPermission)">

                    <!-- SETTINGS -->
                    <v-col cols="3">
                        <v-scale-transition>
                            <title-card title="Template">

                                <v-select
                                    v-if="emailModel.id"
                                    v-model="emailModel"
                                    :items="emails"
                                    item-text="name"
                                    item-value="type"
                                    :return-object="true"
                                    label="Select email template"
                                />

                                <v-select
                                    v-model="localeModel"
                                    :items="subPanel.locales"
                                    item-text="code"
                                    item-value="code"
                                    label="Select locale"
                                    @change="localeChanged"
                                />

                            </title-card>
                        </v-scale-transition>

                        <v-scale-transition>

                            <title-card v-if="settings.actionButtonColor" title="Settings" class="pt-2">
                                <v-select
                                    v-model="settings.actionButtonColor"
                                    :items="actionButtonColors"
                                    label="Action button color"
                                    item-text="name"
                                    item-value="value"
                                    @change="settingsChanged"
                                />

                            </title-card>
                        </v-scale-transition>

                        <v-scale-transition>

                            <title-card title="Elements" no-padding class="pt-2">

                                <div class="pl-5 pr-5 pt-1 pb-1">
                                    <v-switch hide-details
                                              v-model="settings.showHeader"
                                              color="primary"
                                              label="Logo"
                                              @change="settingsChanged"
                                    />

                                    <v-switch hide-details
                                              v-model="settings.showSignature"
                                              color="primary"
                                              label="Signature"
                                              @change="settingsChanged"
                                    />

                                    <v-switch hide-details
                                              v-model="settings.showProjectInfo"
                                              color="primary"
                                              label="Project information"
                                              @change="settingsChanged"
                                    />

                                    <v-switch hide-details
                                              v-model="settings.showFooter"
                                              color="primary"
                                              label="Footer"
                                              class="mb-5"
                                              @change="settingsChanged"
                                    />

                                </div>
                            </title-card>
                        </v-scale-transition>

                    </v-col>

                    <!-- PREVIEW -->
                    <v-col cols="9">

                        <v-scale-transition>
                            <title-card v-show="!loading"
                                        :no-padding="true"
                                        :loading="loadingLocales"
                                        title="Preview">

                                <template v-slot:buttons>



                                </template>

                                <!-- EMAIL SUBJECT AND SENDER NAME -->
                                <v-container class="pt-1" v-if="emailModel.translations.subject !== null">
                                    <v-row dense>
                                        <v-col cols="8">
                                            <v-text-field v-model="emailModel.translations.subject"
                                                          class="pl-2 pt-0"
                                                          hint="Subject"
                                                          @change="unsavedTranslations = true"
                                                          persistent-hint>
                                            </v-text-field>
                                        </v-col>
                                        <v-col cols="4">
                                            <v-text-field v-model="emailModel.translations.sender_name"
                                                          class="pr-2 pt-0"
                                                          hint="Sender name"
                                                          @change="unsavedTranslations = true"
                                                          persistent-hint>
                                            </v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-container>

                                <!-- EMAIL THEME -->
                                <email-theme v-if="emailModel.type && settings"
                                             :type="emailModel.type"
                                             :sub-panel="subPanel"
                                             :locale="localeModel"
                                             :email-translations="emailModel.translations"
                                             :sub-panel-translations="subPanelTranslations"
                                             :settings="settings"
                                             class="email"
                                             :project="projectInfo"
                                             @subpanel:translations="updateSubPanelTranslations"
                                             @email:translations="updateEmailTranslations"
                                />

                                <!-- CONFIRMATION DIALOG FOR UNSAVED CHANGES -->
                                <confirmation-dialog v-model="warningDialogModel"
                                                     headline="WARNING: You have unsaved changes!"
                                                     description="You have unsaved changes! Make sure translations and/or settings are saved for this template. Changes will be lost if you exit!"
                                                     confirm-button-text="Exit"
                                                     abort-button-text="Abort"
                                                     @selected="confirmDialogSelected">
                                </confirmation-dialog>

                            </title-card>
                        </v-scale-transition>
                    </v-col>

                </v-row>

            </v-container>

        </v-card>
    </div>
</template>

<script>

import {mapActions, mapGetters} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import SubPanel from "../ts/SubPanel";
import TitleCard from "../../../components/UI/TitleCard";
import Json from 'vue-json-pretty';
import ColorPicker from "../../../components/UI/ColorPicker";
import EmailTheme from "../../../components/UI/EmailTheme/index";
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";

export default {
    mixins: [activeSessionChecker],

    components: {
        ConfirmationDialog,
        MaterialHeader,
        TitleCard,
        Json,
        ColorPicker,
        EmailTheme
    },

    props: {
        subPanel: {
            required: true,
            type: SubPanel,
        }
    },

    data() {
        return {
            minPermission: 'update sub panels',
            loading: false,
            loadingLocales: false,
            saving: false,
            localeModel: null,
            emailModel: {
                id: null,
                name: null,
                type: null,
                translations: {
                    body: null,
                    action_button_text: null,
                    signature_block: null,
                    subject: null,
                    sender_name: null,
                }
            },
            iframeSource: null,
            emails: null,
            subPanelTranslations: null,
            unsavedTranslations: false,
            translationsSaving: false,
            unsavedSettings: false,
            subject: null,
            senderName: null,
            settings: {
                showHeader: true,
                showSignature: true,
                showProjectInfo: true,
                actionButtonColor: 'blue',
                showFooter: true,
            },
            actionButtonColors: [
                {name: 'Blue', value: 'blue'},
                {name: 'Green', value: 'green'},
                {name: 'Red', value: 'red'}
            ],
            projectInfo: {
                reward_completed: 100,
                loi: 15,
                mobile_friendly: false,
                tablet_friendly: true,
                desktop_friendly: true,
            },
            warningDialogModel: false,
        }
    },

    computed: {
        ...mapGetters('SubPanels', [
            'findById'
        ]),
    },

    mounted() {
        this.settings = Object.assign(this.settings, this.subPanel.email_settings);
        this.localeModel = this.subPanel.locales[0].code;
        this.loading = true;
        this.getEmailTemplates({id: this.subPanel.id, locale: this.localeModel});
    },

    methods: {
        ...mapActions('SubPanels', [
            'loadEmailTemplates',
            'saveEmailSettings',
            'saveEmailTranslations',
        ]),

        getEmailTemplates(config) {

            this.loadEmailTemplates(config)
                .then((response) => {
                    this.emails = [...response.data.emails];
                    this.emailModel = this.emails[0];
                    this.subPanelTranslations = response.data.subPanelTranslations;
                })
                .catch(() => {
                    this.$emit('flash-message', {
                        type: 'error',
                        message: 'We are sorry! Email templates failed to load!'
                    });
                })
                .finally(() => {
                    this.loading = false;
                    this.loadingLocales = false;
                })
        },

        updateSubPanelTranslations(translations) {
            this.unsavedTranslations = true;
            this.subPanelTranslations = translations;
        },

        updateEmailTranslations(translations) {

            this.unsavedTranslations = true;

            const index = this.emails.findIndex(email => email.id === this.emailModel.id);

            if (index === null) {
                throw new Error('Index not found')
            }

            this.emailModel.translations = Object.assign({}, this.emailModel.translations, translations);
            this.emails.splice(index, 1, this.emailModel);
        },

        settingsChanged() {
            this.unsavedSettings = true;
        },

        saveChanges() {
            this.saving = true;
            if (this.unsavedSettings) this.saveSettings();
            if (this.unsavedTranslations) this.saveTranslations();
        },

        saveSettings() {

            this.saveEmailSettings({subPanel: this.subPanel, settings: this.settings})
                .then(() => {
                    this.unsavedSettings = false;
                    Bus.$emit('flash-message', {
                        type: 'info',
                        message: 'Changes saved!'
                    });
                })
                .catch((error) => {

                    if (error.response.status === 422) {

                        this.displayFirstLaravelError(error);

                    } else {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'We are sorry! Email template settings failed to save!'
                        });
                    }
                })
                .finally(() => {
                    this.saving = false;
                })
        },

        saveTranslations() {

            const data = {
                id: this.subPanel.id,
                locale: this.localeModel,
                data: {
                    type: this.emailModel.type,
                    translations: this.emailModel.translations,
                    subPanelTranslations: this.subPanelTranslations,
                }
            };

            this.saveEmailTranslations(data)
                .then(() => {
                    Bus.$emit('flash-message', {
                        message: 'Changes saved!'
                    });

                    this.unsavedTranslations = false;
                })
                .catch((error) => {

                    if (error.response.status === 422) {

                        this.displayFirstLaravelError(error);

                    } else {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'We are sorry! Email template translations failed to save!'
                        });
                    }
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        localeChanged() {
            this.loadingLocales = true;
            this.getEmailTemplates({id: this.subPanel.id, locale: this.localeModel});
        },

        getSubPanelFromStore() {
            if (this.$route.params.id) {
                this.subPanel = this.findById(this.$route.params.id);
            }
        },

        exitClicked() {

            if (this.unsavedTranslations || this.unsavedSettings) {
                this.warningDialogModel = true;
            } else {
                this.$emit('close');
            }
        },

        confirmDialogSelected(exit) {
            if (exit) {
                this.$emit('close');
            }
        }
    }
}
</script>

<style>
.email {
    font-family: Avenir, Helvetica, sans-serif;
    font-size: 1.1em;
}
</style>
